import { useMemo } from 'react';
import { useXp } from '../../context/XpContext';
import TanoCharacter from '../../components/Eleve/TanoCharacter';
import { BLASONS, evalBlason, maxFlamme } from '../../utils/blasons';

/**
 * Onglet « Blasons » — trophées calculés front-only depuis le profil XP et les
 * stats d'évaluations (aucun endpoint dédié). Acquis = liseré doré ; verrouillé
 * = grisé + barre de progression « 3/5 ».
 */
function BlasonsEleve({ stats }) {
    const { profil } = useXp();

    const evalues = useMemo(() => {
        const ctx = { profil, stats, recordFlamme: maxFlamme(profil) };
        return BLASONS.map(b => ({ ...b, ...evalBlason(b, ctx) }));
    }, [profil, stats]);

    const nbAcquis = evalues.filter(b => b.acquis).length;

    return (
        <div className="flex-1 w-full px-4 py-5 max-w-md xl:max-w-3xl mx-auto">
            {/* En-tête : compteur + Tano */}
            <div className="flex items-center gap-3 mb-5">
                <TanoCharacter
                    plume={profil?.skin_plumage}
                    acc={profil?.skin_accessoire}
                    aura={profil?.skin_aura}
                    mood={nbAcquis > 0 ? 'joy' : 'idle'}
                    size={64}
                />
                <div>
                    <h1 className="text-white text-xl font-extrabold" style={{ fontFamily: 'Fredoka' }}>🛡️ Mes blasons</h1>
                    <p className="text-sm font-bold" style={{ color: '#8FD6F5' }}>
                        {nbAcquis} / {evalues.length} débloqués
                    </p>
                </div>
            </div>

            {/* Grille des blasons */}
            <div className="grid grid-cols-2 xl:grid-cols-3 gap-3">
                {evalues.map(b => (
                    <div
                        key={b.id}
                        className="rounded-2xl p-4 text-center relative"
                        style={{
                            background: b.acquis ? 'rgba(255,185,56,.10)' : 'rgba(255,255,255,.05)',
                            border: `1.5px solid ${b.acquis ? 'rgba(255,185,56,.55)' : 'rgba(255,255,255,.10)'}`,
                            boxShadow: b.acquis ? '0 8px 22px rgba(255,185,56,.12)' : 'none',
                        }}
                    >
                        <div
                            className="text-4xl mb-1"
                            style={b.acquis ? {} : { filter: 'grayscale(1)', opacity: 0.4 }}
                            aria-hidden="true"
                        >
                            {b.emoji}
                        </div>
                        {!b.acquis && <span className="absolute top-2.5 right-3 text-xs">🔒</span>}
                        <p className="font-bold text-sm text-white" style={{ fontFamily: 'Fredoka' }}>{b.titre}</p>
                        <p className="text-[11px] mt-0.5 leading-snug" style={{ color: 'rgba(255,255,255,.6)' }}>{b.desc}</p>

                        {b.acquis ? (
                            <p className="mt-2 text-[11px] font-extrabold" style={{ color: '#FFB938' }}>Débloqué !</p>
                        ) : (
                            <div className="mt-2">
                                <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,.12)' }}>
                                    <div
                                        className="h-full rounded-full"
                                        style={{ width: `${Math.round(b.progress * 100)}%`, background: 'linear-gradient(90deg,#29B6E8,#FFB938)', transition: 'width .6s ease-out' }}
                                    />
                                </div>
                                <p className="mt-1 text-[10px] font-bold tabular-nums" style={{ color: 'rgba(255,255,255,.5)' }}>
                                    {b.valeur} / {b.cible}{b.unite ? ` ${b.unite}` : ''}
                                </p>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            <p className="mt-5 text-center text-[11px]" style={{ color: 'rgba(255,255,255,.4)' }}>
                D'autres blasons arrivent bientôt — continue de jouer pour tous les débloquer !
            </p>
        </div>
    );
}

export default BlasonsEleve;
